import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = 'https://api.fluidstream.co';

export default function Dashboard({ auth, onLogout }) {
  const [units,   setUnits]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnits();
    const interval = setInterval(fetchUnits, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line
  }, []);

  const fetchUnits = async () => {
    try {
      const res = await axios.get(`${API}/units`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setUnits(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  // Role model is viewer | operator | superadmin. 'admin' was removed from the
  // API — checking for it here left this permanently false and the grouped
  // view below unreachable.
  const user      = auth.user || {};
  const isAdmin   = user.role === 'superadmin';

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <img src="/logo-white.png" alt="Fluidstream" style={styles.logoImg} />
          <div style={styles.headerDivider} />
          <div>
            <p style={styles.headerRole}>{isAdmin ? 'Admin Dashboard' : 'Customer Portal'}</p>
            <p style={styles.headerUser}>{user.name || user.username || ''}</p>
          </div>
        </div>
        <button style={styles.logoutBtn} onClick={onLogout}>Sign Out</button>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{units.length}</div>
          <div style={styles.statLabel}>Total Units</div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statNumber, color: '#1a73b8'}}>
            {units.filter(u => u.active).length}
          </div>
          <div style={styles.statLabel}>Active</div>
        </div>
        {isAdmin && (
          <div style={styles.statCard}>
            <div style={styles.statNumber}>
              {new Set(units.map(u => u.company_slug).filter(Boolean)).size}
            </div>
            <div style={styles.statLabel}>Customers</div>
          </div>
        )}
      </div>

      <div style={styles.content}>
        {loading && <div style={styles.message}>Loading units...</div>}
        {error   && <div style={styles.errorMsg}>{error}</div>}
        {!loading && units.length === 0 && (
          <div style={styles.message}>No units assigned to your account.</div>
        )}

        {isAdmin ? (
          (() => {
            const grouped = units.reduce((acc, u) => {
              const key = u.company_name || 'Unassigned';
              if (!acc[key]) acc[key] = [];
              acc[key].push(u);
              return acc;
            }, {});
            return Object.entries(grouped).map(([customer, cuUnits]) => (
              <div key={customer} style={styles.customerGroup}>
                <h2 style={styles.customerTitle}>{customer}</h2>
                <div style={styles.grid}>
                  {cuUnits.map(unit => (
                    <UnitCard key={unit.unit_id} unit={unit} onClick={() => navigate(`/unit/${unit.unit_id}`)} />
                  ))}
                </div>
              </div>
            ));
          })()
        ) : (
          <div style={styles.grid}>
            {units.map(unit => (
              <UnitCard key={unit.unit_id} unit={unit} onClick={() => navigate(`/unit/${unit.unit_id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UnitCard({ unit, onClick }) {
  return (
    <div style={styles.card} onClick={onClick}>
      <div style={styles.cardHeader}>
        <div style={{...styles.statusDot, background: unit.active ? '#1a73b8' : '#ccc'}} />
        <span style={styles.unitId}>{unit.unit_id}</span>
      </div>
      <h3 style={styles.unitName}>{unit.name}</h3>
      <p style={styles.unitLocation}>{unit.location || 'No location set'}</p>
      <div style={styles.cardFooter}>
        <span style={styles.viewBtn}>View Details →</span>
      </div>
    </div>
  );
}

const styles = {
  page:          { minHeight: '100vh', background: '#EEF4FB' },
  header:        { background: '#1a2a4a', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft:    { display: 'flex', alignItems: 'center', gap: 16 },
  logoImg:       { height: 30, width: 'auto' },
  headerDivider: { width: 1, height: 36, background: 'rgba(255,255,255,0.2)' },
  headerRole:    { fontSize: 10, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1.5 },
  headerUser:    { fontSize: 14, color: '#fff', fontWeight: 600, marginTop: 2 },
  logoutBtn:     { padding: '8px 18px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 14 },
  statsBar:      { background: '#fff', padding: '16px 32px', display: 'flex', gap: 32, borderBottom: '1px solid #dce8f5' },
  statCard:      { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  statNumber:    { fontSize: 28, fontWeight: 700, color: '#1a2a4a' },
  statLabel:     { fontSize: 12, color: '#888', marginTop: 2 },
  content:       { padding: '24px 32px' },
  message:       { textAlign: 'center', color: '#888', padding: 40 },
  errorMsg:      { background: '#fff0f0', border: '1px solid #ffcccc', borderRadius: 8, padding: 16, color: '#cc0000', marginBottom: 16 },
  customerGroup: { marginBottom: 32 },
  customerTitle: { fontSize: 15, fontWeight: 600, color: '#1a2a4a', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #dce8f5' },
  grid:          { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
  card:          { background: '#fff', borderRadius: 12, padding: 20, cursor: 'pointer', boxShadow: '0 2px 12px rgba(26,115,180,0.08)', border: '1px solid #dce8f5' },
  cardHeader:    { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  statusDot:     { width: 10, height: 10, borderRadius: '50%' },
  unitId:        { fontSize: 12, color: '#999', fontFamily: 'monospace' },
  unitName:      { fontSize: 16, fontWeight: 600, color: '#1a2a4a', marginBottom: 4 },
  unitLocation:  { fontSize: 13, color: '#888', marginBottom: 12 },
  cardFooter:    { borderTop: '1px solid #eef4fb', paddingTop: 10 },
  viewBtn:       { fontSize: 13, color: '#1a73b8', fontWeight: 600 },
};
